import { DateTime } from "luxon";

import { Element } from "@api/models/Element";
import Vue from 'vue';
import { EventMembership } from "@api/models/EventMemberShip";
import { SessionRepository } from "@api/repositories/SessionRepository";
import { WebinarInfo } from "@api/models/WebinarInfo";
import { RepositoryFabric } from "@api/repositories/RepositoryFabric";
import { EventInterface } from "@share/program/types/EventInterface";

import { Rating } from "@api/models/Rating";
import { RatingRepository } from "@api/repositories/RatingRepository";

import { dateInOriginalOffset } from "@common/dateTimeHelper";
import { Store } from "vuex";
import VueI18n from "vue-i18n";
import { isWebinarNow, secondsBeforeMoment } from "@common/webinarHelper";
import { DataLoader } from "./DataLoader";
import { ProgramData } from "../types/ProgramData";
import vueUpdateArray from '@common/vueUpdateArray';
import { FetchDataParams } from "../types/FetchData";

import { COMINGSOON_PERIOD_IN_MINUTES } from "@common/constants";
import { getDates, isToday } from "./dates";
import { ProgramDataMapper } from "./ProgramDataMapper";
import { AgendaDistributer } from "./agendaDistribution";
import {isAgendaNotForDisplay} from "@common/AgendaHelper";
import { SignalRObserversHelper } from "@share/program/helpers/SignalRObserversHelper";
import { cyrb53 } from "@common/stringHash";
import debounce from "lodash.debounce";

export class ProgramDataHelper {
    private readonly sessionRepository: SessionRepository;
    private readonly ratingRepository: RatingRepository;

    eventId: string;
    userId: number;
    allAgendas: Element[];

    speakers: { [key: string]: EventMembership[] };
    eventSpeakers: { [key: string]: EventMembership[] };
    webinarsInfo: { [key: string]: WebinarInfo };
    agendaSchema: string;
    eventSchema: string;
    backend: string;
    _date: DateTime | null;
    likes: { [key: string]: Rating } = {};
    eventLikes: Rating;
    comments: Record<string, number> = {};
    eventComments: Record<string, number> = {};
    onceLoaded = false;
    agendaUpdating = false;

    agendaResource: EventInterface[] = [];
    upcomingAgendaResource: EventInterface[] = [];
    pastAgendaResource: EventInterface[] = [];
    offset: number;
    isExpired = false;
    store: Store<any>;
    i18: VueI18n | null = null;

    dataLoader: DataLoader;
    signalRHelper: SignalRObserversHelper;

    /**
     * hash of last updated (by realtime) agenda
     */
    lastHash: number | null = null;
    updateSpeakersHandler: ((agendaIds: string[]) => void) | null = null;

    constructor(
        eventId: string,
        repositories: RepositoryFabric,
        store: Store<any>,
        i18: VueI18n
    ) {
        this.eventId = eventId;
        this.backend = store.state.session.backend;
        this.userId = store.state.session.userId;
        this.sessionRepository = repositories.get(RepositoryFabric.SESSION);
        this.agendaSchema = process.env.VUE_APP_AGENDA_SCHEMA!;
        this.eventSchema = process.env.VUE_APP_EVENTS_SCHEMA!;
        this.ratingRepository = repositories.get(RepositoryFabric.RATING);
        this.offset = store.state.timeZoneOffset || 0;
        this.date = null;
        this.store = store;
        this.i18 = i18;
        this.dataLoader = new DataLoader(repositories, store);

        this.allAgendas = [];

        this.speakers = {};
        this.eventSpeakers = {};
        this.webinarsInfo = {};
        this.likes = {};

        this.signalRHelper = new SignalRObserversHelper(repositories.get(RepositoryFabric.REALTIME), this);

        this.updateSpeakersHandler = debounce((agendaIds: string[]) => {
            console.log(agendaIds);
            this.fetchSpeakers(agendaIds);
        }, Math.round(Math.random() * 9000))
    }

    get date(): DateTime | null {
        return this._date;
    }

    set date(value: DateTime | null) {
        this._date = value ? dateInOriginalOffset(value, this.offset) : value;
    }

    get lang() {
        return this.store.state.session.language || 'en';
    }

    get filterOfflineSessions() {
        return !this.store.getters['events/selectedEventViewParam']('stage', null)?.showOfflineSessions || false
    }

    async fetchData() {

        const today = dateInOriginalOffset(DateTime.local(), this.offset);
        const dates = getDates(today);

        const params: FetchDataParams = {
            eventId: this.eventId,
            dayStart: dates.dayStart,
            dayEnd: dates.dayEnd,
            filterOffline: this.filterOfflineSessions,
            loadEventData: isToday(this.date, this.offset),
            loadAdditionalData: window.location.hash === "#favorites" || !this.onceLoaded
        }

        const data = await this.dataLoader.fetchData(params, this.store.getters['groupFilter']);

        this.onceLoaded = true;
        this.setData(data);

        if (this.store.state.program.useRealtimeUpdates) {
            //@todo shouldn't be in each program helper
            this.signalRHelper.connect(this.lang);
        }
    }

    async fetchSpeakers(agendaIds: string[]) {
        const speakers: { [key: string]: EventMembership[] } = await this.dataLoader.fetchSpeakers(agendaIds);
        this.setSpeakers(speakers);
    }

    setData(data: ProgramData) {

        this.isExpired = false;

        vueUpdateArray(this.allAgendas, data.allAgendas);

        // if (data?.exhibitions) {
        //     vueUpdateArray(this.exhibitions, data.exhibitions);
        // }

        Vue.set(this, 'speakers', data.speakers);

        //спикеры берутся из сущности event т.к. заодно там получаются и id всех спикеров мероприятия
        const speakers = this.store.getters["events/selectedEvent"]?.speakersProfiles;
        Vue.set(this, 'eventSpeakers', { [this.eventId]: speakers });

        //обновлять только если данные были загружены
        data.webinarInfos && Vue.set(this, 'webinarsInfo', data.webinarInfos);
        data.likes && Vue.set(this, 'likes', data.likes);
        data.comments && Vue.set(this, 'comments', data.comments);

        Vue.set(this, 'eventLikes', data.eventLikes);
        Vue.set(this, 'eventComments', data.eventComments);

        this.prepareAgendas();

        setTimeout(() => {
            this.isExpired = true;
        }, 15 * 60 * 1000);
    }

    private setSpeakers(speakers: { [key: string]: EventMembership[] }) {
        const agendaIds = Object.keys(speakers);

        agendaIds.forEach((agendaId) => {
            Vue.set(this.speakers, agendaId, speakers[agendaId]);
        });

        this.prepareAgendas();
    }

    private prepareAgendaForGrid(agendas?: Element[]) {

        if (!agendas?.length) {
            const today = dateInOriginalOffset(DateTime.local(), this.offset);
            const { dayStart, dayEnd } = getDates(today);
            agendas = AgendaDistributer.getTodayAgendas(this.allAgendas, dayStart, dayEnd)
        }

        Vue.set(this, 'agendaResource', agendas.map((item) =>
            ProgramDataMapper.toEventInterface(item, this.speakers, this.webinarsInfo, this.likes, this.comments))
        );
    }

    private prepareUpcomingAgenda(agendas?: Element[]) {

        if (!agendas?.length) {
            const today = dateInOriginalOffset(DateTime.local(), this.offset);
            const { dayStart } = getDates(today);
            agendas = AgendaDistributer.getFtureAgendas(this.allAgendas, dayStart)
        }

        const upcomingAgendas = agendas
            .filter(item => Math.round(secondsBeforeMoment(item.beginAt)) >= COMINGSOON_PERIOD_IN_MINUTES * 60)
            .map(item => ProgramDataMapper.getCommonAgenda(item, this.speakers[item.id], true))

        Vue.set(this, 'upcomingAgendaResource', upcomingAgendas)
    }

    private preparePastAgenda(agendas?: Element[]) {

        if (!agendas?.length) {
            agendas = AgendaDistributer.getPastAgendas(this.allAgendas);
        }

        Vue.set(this, 'pastAgendaResource', agendas.map(item => ProgramDataMapper.getCommonAgenda(item, this.speakers[item.id], false)))
    }

    private prepareAgendas() {
        const todayDate = dateInOriginalOffset(DateTime.local(), this.offset);
        const { dayStart, dayEnd } = getDates(todayDate);

        const { past, today, future } = AgendaDistributer.distribute(this.allAgendas, dayStart, dayEnd);

        this.prepareAgendaForGrid(today);
        this.prepareUpcomingAgenda(future);
        this.preparePastAgenda(past);
    }

    get todayAgendas() {
        const today = dateInOriginalOffset(DateTime.local(), this.offset);
        const { dayStart, dayEnd } = getDates(today);
        return AgendaDistributer.getTodayAgendas(this.allAgendas, dayStart, dayEnd);
    }

    async toggleAgendaLike(agendaId: string, value: number | null, needUpdate = true) {
        await this.ratingRepository.rate(this.agendaSchema, agendaId, value);

        if (needUpdate) {
            this.updateLike(agendaId, value);
        }
    }

    setEventLike(value: number | null) {
        this.ratingRepository.rate(this.eventSchema, this.eventId, value);
    }

    updateEventLike(value: number | null) {

        const updatedLike = this.commonUpdateLike(this.eventLikes, value);

        Vue.set(this, 'eventLikes', updatedLike);
    }

    updateLikeIfNeeded(sourceLike: Rating, existingLike: Rating) {

        if (existingLike.count !== sourceLike.count) {

            existingLike.count = sourceLike.count;
            existingLike.rating = sourceLike.rating;

            return true;
        }
        else {
            return false;
        }
    }

    updateEventLikeRealtime(sourceLike: Rating) {
        const like = this.eventLikes;

        if (like && this.updateLikeIfNeeded(sourceLike, like)) {
            Vue.set(this, 'eventLikes', like);
        }
    }

    updateLikeRealTime(agendaId: string, sourceLike: Rating) {

        const like = this.likes[agendaId];

        if(like) {

            const updated = this.updateLikeIfNeeded(sourceLike, like);

            if(updated) {
                this.setLikeToObject(agendaId, like);
                this.prepareAgendaForGrid();
            }
        }
    }

	setLikeToObject(objectId: string, like: Rating) {
        Vue.set(this.likes, objectId, like);
    }

    updateLike(agendaId: string, value: number | null) {
        const like = this.likes[agendaId];

        if (like) {
            const updatedLike = this.commonUpdateLike(like, value);

            Vue.set(this.likes, agendaId, updatedLike);
            this.prepareAgendaForGrid();
        }
    }

    commonUpdateLike(like: Rating, value: number | null) {
        if (value) {
            like.count = like.count ? (like.count + 1) : 1;
        }
        else {
            like.count = like.count ? (like.count - 1) : 0;
        }
        like.rating = value;
        return like;
    }

    incrementCommentsAmount(agendaId: string) {
        this.comments[agendaId] = this.comments[agendaId] ? this.comments[agendaId] + 1 : 1;
        this.prepareAgendaForGrid();
    }

    updateCommentsCount(objectId: string, comments: number) {
        Vue.set(this.comments, objectId, comments);
        this.prepareAgendaForGrid();
    }

    /**
     * if session is live now then fetch webinar info
     */
    async updateAgendas() {
        const ids: string[] = [],
            previousLength = Object.keys(this.webinarsInfo).length;

        const today = dateInOriginalOffset(DateTime.local(), this.offset);
        const { dayStart, dayEnd } = getDates(today);

        const agendas = AgendaDistributer.getTodayAgendas(this.allAgendas, dayStart, dayEnd)

        agendas.forEach(item => {
            const isNow = item.beginAt && item.endAt && isWebinarNow(item.beginAt, item.endAt);
            if (isNow && !this.webinarsInfo[item.id]) {
                ids.push(item.id)
            }
            else if (!isNow && this.webinarsInfo[item.id]) {
                Vue.delete(this.webinarsInfo, item.id);
            }
        })

        if (ids.length) {
            const webinarInfos = await this.sessionRepository.webinar(this.agendaSchema, ids);
            const newObject = Vue.observable(Object.assign(this.webinarsInfo, webinarInfos));
            Vue.set(this, 'webinarsInfo', newObject);
        }

        if (Object.keys(this.webinarsInfo).length != previousLength) {
            this.prepareAgendaForGrid();
        }
    }

    updateOrCreateOrRemoveAgenda(agendaData: any, schemaId: string) {

        const dataStr = JSON.stringify(agendaData);

        //prevent update session if last session is equal to current session
        if (cyrb53(dataStr) === this.lastHash) {
            return
        }

        this.lastHash = cyrb53(dataStr);

        const session: Element = new Element(agendaData, schemaId);

        // do not update offline session if filtering offline session is enabled
        if (this.filterOfflineSessions && !session.onlineProvider) {
            return
        }

        const originAgendas = this.allAgendas; //change name for futureAgendas

        let newAgendas: Element[] = [];

        //REMOVE:
        if (isAgendaNotForDisplay(session, this.eventId, this.store.getters.groupIds)) {
            newAgendas = originAgendas.filter((item) => item.id !== session.id);
        }
        //ADD:
        //UPDATE:
        else if (session.eventId === this.eventId) {
            newAgendas = [session, ...originAgendas];

            //updating speakers
            this.updateSpeakersHandler?.([session.id])
        }

        vueUpdateArray(this.allAgendas, newAgendas);

        this.prepareAgendas();
    }

    deleteSession(id: string) {
        return this.dataLoader.deleteAgenda(id);
    }

    getTodayAgendas(agendas: Element[], date: DateTime | null) {
        const targetDate = date || DateTime.local();
        const startOfDay = targetDate.startOf("day");
        const endOfDay = targetDate.endOf("day");

        return agendas.filter((session) => {
            const beginAt = DateTime.fromISO(session.beginAt);
            return beginAt >= startOfDay && beginAt <= endOfDay;
        })
    }

    get exhibitions(): Element[] {
        return Object.values(this.store.getters['events/selectedEventExhibitions']);
    }

    get exhibitionIds(): string[] {
        return this.store.getters['events/exhibitionIds'](this.eventId);
    }
}
